import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import Header from '../../../components/header';
import ParentWrapper from '../../../components/ParentWrapper';
import Loader from '../../../components/AnimatedLoader';
import NoData from '../../../components/NoData';
import {
  nipponCompactLegendText,
  nipponPhaseInterpretationText,
  nipponRatingNaItem
} from './nipponRatingLegend';

const formatDuration = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.round((Number(milliseconds) || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};

const formatDateTime = (value) => {
  if (!value) return 'Not Available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not Available'
    : date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
};

const StatCard = ({ label, value }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #eceff3', height: '100%' }}>
    <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>{value}</Typography>
  </Paper>
);

const scoreColorMap = {
  Strong: { bg: '#E8F5E9', color: '#1B5E20' },
  Moderate: { bg: '#FFF8E1', color: '#B45309' },
  Weak: { bg: '#FFEBEE', color: '#B91C1C' }
};

export default function BotInterviewCompiledReport() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'info' });
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(nipponBotApiEndpoints.compiledReport, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReport(response?.data?.data || null);
      } catch (error) {
        setNotice({ open: true, message: error?.response?.data?.status?.value || 'Failed to load compiled report.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [token]);

  const stats = useMemo(() => ([
    ['Uploaded Calls', report?.kpis?.totalUploadedCalls || 0],
    ['Completed Reports', report?.kpis?.completedReports || 0],
    ['In Progress', report?.kpis?.processingCalls || 0],
    ['Failed', report?.kpis?.failedCalls || 0],
    ['Total Candidates', report?.kpis?.totalCandidates || 0],
    ['Avg Qualification', `${report?.kpis?.averageQualificationScore || 0}%`],
    ['Avg Call Duration', formatDuration(report?.kpis?.averageCallDuration || 0)]
  ]), [report]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axiosInstance.get(nipponBotApiEndpoints.compiledReportExport, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nippon-bot-compiled-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setNotice({ open: true, message: error?.response?.data?.status?.value || 'Failed to export compiled report.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <ParentWrapper sx={{ minHeight: "calc(100vh - 45px)" }}><Loader /></ParentWrapper>;
  }

  const candidates = report?.candidates || [];
  const paginatedCandidates = candidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (_event, nextPage) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <ParentWrapper sx={{ minHeight: "calc(100vh - 45px)" }}>
      <Header title="Compiled Bot Interview Report" subtitle="Review completed candidate interview analysis in one place" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/user/dashboard')}
            sx={{ textTransform: 'none' }}
          >
            Return To Dashboard
          </Button>
          <Button
            variant={pathname === '/user/bot-interviews/compiled-report' ? 'contained' : 'outlined'}
            disabled={pathname === '/user/bot-interviews/compiled-report'}
            onClick={() => navigate('/user/bot-interviews/compiled-report')}
            sx={{ textTransform: 'none' }}
          >
            Open Full Compiled Report
          </Button>
          <Button
            variant={pathname === '/user/bot-interviews/candidates' ? 'contained' : 'outlined'}
            disabled={pathname === '/user/bot-interviews/candidates'}
            onClick={() => navigate('/user/bot-interviews/candidates')}
            sx={{ textTransform: 'none' }}
          >
            View Candidates
          </Button>
        </Box>
        <Button variant="contained" onClick={handleExport} disabled={exporting || !candidates.length} sx={{ textTransform: 'none' }}>
          {exporting ? <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} /> : null}
          {exporting ? 'Downloading...' : 'Download Excel'}
        </Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map(([label, value]) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={label}>
            <StatCard label={label} value={value} />
          </Grid>
        ))}
      </Grid>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #eceff3', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Bot Performance</Typography>
        <Grid container spacing={2}>
          {[
            ['Profile Fields Collected', report?.botPerformance?.profileFieldsCollected || 0],
            ['Peer References Collected', report?.botPerformance?.peerReferencesCollected || 0],
            ['Competency Topics Assessed', report?.botPerformance?.competencyTopicsAssessed || 0],
            ['M0 Questions Answered', report?.botPerformance?.m0QuestionsAnswered || 0],
            ['Not Applicable Count', report?.botPerformance?.notApplicableCount || 0],
            ['Missing / Unknown Count', report?.botPerformance?.missingOrUnknownCount || 0],
            ['Average Completeness', `${report?.botPerformance?.averageCompleteness || 0}%`],
            ['Average Confidence', `${report?.botPerformance?.averageConfidence || 0}%`]
          ].map(([label, value]) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>{label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #eceff3', mb: 3, backgroundColor: '#fcfcfd' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Rating Legend</Typography>
        <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, mb: 0.75 }}>
          {nipponCompactLegendText}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
          {nipponRatingNaItem.description}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
          {nipponPhaseInterpretationText}
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #eceff3', overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid #eceff3', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Candidate Summary</Typography>
          {!!candidates.length && (
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, candidates.length)} of {candidates.length} candidates
            </Typography>
          )}
        </Box>
        {!candidates.length ? (
          <NoData />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Candidate', 'PAN', 'Recording Date & Time', 'Uploaded On', 'File Name', 'Location', 'Employer', 'Experience', 'CTC', 'Business Value', 'Competency', 'M0', 'Qualification', 'Band', 'Profile Completeness', 'Peers', 'Report'].map((label) => (
                      <TableCell key={label} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCandidates.map((candidate) => {
                    const scoreColors = scoreColorMap[candidate.qualificationBand] || scoreColorMap.Moderate;
                    return (
                      <TableRow key={candidate.transcriptionId || candidate.recordingId} hover>
                        <TableCell>{candidate.candidateName}</TableCell>
                        <TableCell>{candidate.panMasked}</TableCell>
                        <TableCell>{formatDateTime(candidate.recordingDateTime)}</TableCell>
                        <TableCell>{formatDateTime(candidate.uploadedDateTime)}</TableCell>
                        <TableCell>{candidate.originalFileName || 'Unknown'}</TableCell>
                        <TableCell>{candidate.city && candidate.city !== candidate.location ? `${candidate.location}, ${candidate.city}` : candidate.location}</TableCell>
                        <TableCell>{candidate.employer}</TableCell>
                        <TableCell>{candidate.experience}</TableCell>
                        <TableCell>{candidate.ctc}</TableCell>
                        <TableCell>{candidate.businessValue}</TableCell>
                        <TableCell>{candidate.competencyScore}%</TableCell>
                        <TableCell>{candidate.m0ReadinessScore}%</TableCell>
                        <TableCell>{candidate.qualificationScore}%</TableCell>
                        <TableCell>
                          <Chip label={candidate.qualificationBand} size="small" sx={{ backgroundColor: scoreColors.bg, color: scoreColors.color, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>{candidate.profileCompleteness}%</TableCell>
                        <TableCell>{candidate.peerReferenceCount}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={() => window.open(`/user/audio-analytics/${candidate.transcriptionId}`, '_blank')}
                            sx={{ textTransform: 'none' }}
                          >
                            View Report
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={candidates.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
      <Snackbar open={notice.open} autoHideDuration={5000} onClose={() => setNotice((current) => ({ ...current, open: false }))}>
        <Alert severity={notice.severity} onClose={() => setNotice((current) => ({ ...current, open: false }))}>{notice.message}</Alert>
      </Snackbar>
    </ParentWrapper>
  );
}
